<section class="grid gap-4">
  <div class="flex items-center justify-between gap-3">
    <page-title title="Suicide risk screener" />
    <a mat-stroked-button [routerLink]="['/interventions', interventionId]">Back to intervention</a>
  </div>

  <mat-card class="rounded-3xl border border-slate-200 p-6 shadow-lg shadow-slate-200/60">
    <form [formGroup]="form" (ngSubmit)="save()" class="grid gap-8">
      <section class="grid gap-3">
        <div class="grid gap-1 text-sm text-slate-700">
          <span><span class="font-medium">Case number:</span> {{ store.selected()?.interventionNumber }}</span>
          @if (screener(); as item) {
            <span><span class="font-medium">Screen completed by:</span> {{ item.screenedByName ?? 'Unknown' }}</span>
          }
        </div>
        <mat-form-field appearance="outline" class="md:max-w-xs">
          <mat-label>Date</mat-label>
          <input matInput [matDatepicker]="screenedPicker" formControlName="screenedDate" [max]="today" />
          <mat-datepicker-toggle matIconSuffix [for]="screenedPicker"></mat-datepicker-toggle>
          <mat-datepicker #screenedPicker></mat-datepicker>
        </mat-form-field>
        <p class="text-sm text-slate-500">
          Open with: "I need to ask you a few questions on how you have been feeling, is that ok?"
        </p>
      </section>

      <section class="grid gap-6">
        @for (item of feelings; track item.field) {
          <div class="grid gap-2">
            <h3 class="font-semibold text-slate-900">{{ item.number }}. {{ item.question }} <span class="text-red-600">*</span></h3>
            <mat-radio-group [formControlName]="item.field" class="flex flex-wrap gap-x-4">
              @for (option of frequencies(); track option.id) {
                <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
              }
            </mat-radio-group>
          </div>
        }
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">5. Have you ever attempted suicide? <span class="text-red-600">*</span></h3>
        <mat-radio-group formControlName="attemptedSuicide" class="flex gap-4">
          <mat-radio-button [value]="true">Yes</mat-radio-button>
          <mat-radio-button [value]="false">No</mat-radio-button>
        </mat-radio-group>
        @if (form.controls.attemptedSuicide.value) {
          <div class="grid gap-4 border-l-4 border-slate-200 pl-4">
            <div class="grid gap-2">
              <span class="text-sm font-medium text-slate-800">a. How many times have you attempted suicide?</span>
              <mat-radio-group formControlName="attemptCountId" class="flex flex-wrap gap-x-4">
                @for (option of attemptCounts(); track option.id) {
                  <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
                }
              </mat-radio-group>
            </div>
            <div class="grid gap-2">
              <span class="text-sm font-medium text-slate-800">b. How long ago was the last attempt?</span>
              <mat-radio-group formControlName="lastAttemptId" class="flex flex-wrap gap-x-4">
                @for (option of lastAttempts(); track option.id) {
                  <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
                }
              </mat-radio-group>
            </div>
            <mat-form-field appearance="outline">
              <mat-label>Have things changed since?</mat-label>
              <input matInput maxlength="500" formControlName="changesSinceAttempt" />
            </mat-form-field>
          </div>
        }
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">6. Have you gone through any upsetting events recently? <span class="text-red-600">*</span></h3>
        <mat-radio-group formControlName="recentUpsettingEvents" class="flex gap-4">
          <mat-radio-button [value]="true">Yes</mat-radio-button>
          <mat-radio-button [value]="false">No</mat-radio-button>
        </mat-radio-group>
        @if (form.controls.recentUpsettingEvents.value) {
          <div class="grid gap-2 border-l-4 border-slate-200 pl-4">
            <span class="text-sm font-medium text-slate-800">Tick all that apply</span>
            <div class="grid gap-x-4 md:grid-cols-2">
              @for (item of events; track item.field) {
                <mat-checkbox [formControlName]="item.field">{{ item.label }}</mat-checkbox>
              }
            </div>
            <mat-form-field appearance="outline">
              <mat-label>Other</mat-label>
              <input matInput maxlength="200" formControlName="eventOther" />
            </mat-form-field>
          </div>
        }
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">
          7. Have things been so bad lately that you have thought of suicide? <span class="text-red-600">*</span>
        </h3>
        <mat-radio-group formControlName="thoughtsOfSuicide" class="flex gap-4">
          <mat-radio-button [value]="true">Yes</mat-radio-button>
          <mat-radio-button [value]="false">No (skip to 10)</mat-radio-button>
        </mat-radio-group>
        @if (form.controls.thoughtsOfSuicide.value) {
          <div class="grid gap-4 border-l-4 border-slate-200 pl-4">
            <mat-form-field appearance="outline">
              <mat-label>a. How often do you have thoughts of suicide?</mat-label>
              <input matInput maxlength="200" formControlName="thoughtFrequency" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>b. How long have you been having these thoughts?</mat-label>
              <input matInput maxlength="200" formControlName="thoughtDuration" />
            </mat-form-field>
            <div class="grid gap-2">
              <span class="text-sm font-medium text-slate-800">c. How intense are these thoughts when they are most severe?</span>
              <mat-radio-group formControlName="worstThoughtIntensityId" class="flex flex-wrap gap-x-4">
                @for (option of intensities(); track option.id) {
                  <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
                }
              </mat-radio-group>
            </div>
            <div class="grid gap-2">
              <span class="text-sm font-medium text-slate-800">d. How intense have these thoughts been in the last week?</span>
              <mat-radio-group formControlName="recentThoughtIntensityId" class="flex flex-wrap gap-x-4">
                @for (option of intensities(); track option.id) {
                  <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
                }
              </mat-radio-group>
            </div>
          </div>

          <h3 class="font-semibold text-slate-900">8. Do you have a current plan for how you would attempt suicide? <span class="text-red-600">*</span></h3>
          <mat-radio-group formControlName="hasPlan" class="flex gap-4">
            <mat-radio-button [value]="true">Yes</mat-radio-button>
            <mat-radio-button [value]="false">No</mat-radio-button>
          </mat-radio-group>
          @if (form.controls.hasPlan.value) {
            <div class="grid gap-4 border-l-4 border-slate-200 pl-4">
              <mat-form-field appearance="outline">
                <mat-label>a. What method would you use?</mat-label>
                <input matInput maxlength="500" formControlName="planMethod" />
              </mat-form-field>
              <div class="grid gap-2">
                <span class="text-sm font-medium text-slate-800">Access to means?</span>
                <mat-radio-group formControlName="accessToMeans" class="flex gap-4">
                  <mat-radio-button [value]="true">Yes</mat-radio-button>
                  <mat-radio-button [value]="false">No</mat-radio-button>
                </mat-radio-group>
              </div>
              <mat-form-field appearance="outline">
                <mat-label>b. Where would this occur?</mat-label>
                <input matInput maxlength="500" formControlName="planLocation" />
              </mat-form-field>
              <div class="grid gap-2">
                <span class="text-sm font-medium text-slate-800">Have all necessary preparations been made?</span>
                <mat-radio-group formControlName="preparationsMade" class="flex gap-4">
                  <mat-radio-button [value]="true">Yes</mat-radio-button>
                  <mat-radio-button [value]="false">No</mat-radio-button>
                </mat-radio-group>
              </div>
              <div class="grid gap-2">
                <span class="text-sm font-medium text-slate-800">c. How likely are you to act on this plan in the near future?</span>
                <mat-radio-group formControlName="planLikelihoodId" class="flex flex-wrap gap-x-4">
                  @for (option of planLikelihoods(); track option.id) {
                    <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
                  }
                </mat-radio-group>
              </div>
            </div>
          }

          <mat-form-field appearance="outline">
            <mat-label>9. What has stopped you acting on these suicidal thoughts?</mat-label>
            <textarea matInput rows="3" maxlength="1000" formControlName="protectiveFactors"></textarea>
          </mat-form-field>
        }
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">
          10. Do you have any friends/family members you can confide in if you have a serious problem? <span class="text-red-600">*</span>
        </h3>
        <mat-radio-group formControlName="hasSupport" class="flex gap-4">
          <mat-radio-button [value]="true">Yes</mat-radio-button>
          <mat-radio-button [value]="false">No</mat-radio-button>
        </mat-radio-group>
        @if (form.controls.hasSupport.value) {
          <div class="grid gap-4 border-l-4 border-slate-200 pl-4">
            <mat-form-field appearance="outline">
              <mat-label>a. What is/are this/these person/people?</mat-label>
              <input matInput maxlength="500" formControlName="supportPeople" />
            </mat-form-field>
            <div class="grid gap-2">
              <span class="text-sm font-medium text-slate-800">b. How often are you in contact with this/these person/people?</span>
              <mat-radio-group formControlName="supportContactFrequencyId" class="flex flex-wrap gap-x-4">
                @for (option of contactFrequencies(); track option.id) {
                  <mat-radio-button [value]="option.id">{{ option.name }}</mat-radio-button>
                }
              </mat-radio-group>
            </div>
          </div>
        }
        <mat-form-field appearance="outline">
          <mat-label>11. What has helped you through difficult times in the past?</mat-label>
          <textarea matInput rows="3" maxlength="1000" formControlName="pastCoping"></textarea>
        </mat-form-field>
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">Client presentation/statements (tick all that apply)</h3>
        <div class="flex flex-wrap gap-x-4">
          @for (item of presentations; track item.field) {
            <mat-checkbox [formControlName]="item.field">{{ item.label }}</mat-checkbox>
          }
        </div>
        <mat-form-field appearance="outline">
          <mat-label>Other</mat-label>
          <input matInput maxlength="200" formControlName="presentsOther" />
        </mat-form-field>
      </section>

      <section class="grid gap-3">
        <h3 class="font-semibold text-slate-900">Level of risk <span class="text-red-600">*</span></h3>
        @if (autoHigh()) {
          <p class="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            The client is expressing thoughts of suicide and presents as agitated, confused or delusional, so the level of
            risk is automatically High.
          </p>
        }
        <mat-radio-group formControlName="riskLevelCode" class="flex flex-wrap gap-x-4">
          @for (level of riskLevels; track level.code) {
            <mat-radio-button [value]="level.code" [disabled]="!viewing && autoHigh() && level.code !== 'HIGH_RISK'">
              {{ level.label }}
            </mat-radio-button>
          }
        </mat-radio-group>
        @if (selectedLevel(); as level) {
          <div class="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 md:grid-cols-2">
            <div>
              <h4 class="mb-1 font-semibold text-slate-900">{{ level.label }} risk looks like</h4>
              <ul class="list-disc pl-5">
                @for (item of level.indicators; track item) {
                  <li>{{ item }}</li>
                }
              </ul>
            </div>
            <div>
              <h4 class="mb-1 font-semibold text-slate-900">Suggested response</h4>
              <ul class="list-disc pl-5">
                @for (item of level.response; track item) {
                  <li>{{ item }}</li>
                }
              </ul>
            </div>
          </div>
        }
      </section>

      @if (!viewing) {
        <div class="flex justify-end">
          <button mat-flat-button type="submit" [disabled]="!canSave()">
            @if (store.savingRiskScreener()) {
              <mat-spinner diameter="20" class="mr-2" />
            }
            Record screener
          </button>
        </div>
      }

      <p class="text-xs text-slate-500">
        Adapted from the Suicide Assessment Kit (SAK), National Drug and Alcohol Research Centre, University of New South Wales
        (Deady, Ross &amp; Darke, 2011). IW EAP MW19 Suicide Risk Screener ver 2.3.
      </p>
    </form>
  </mat-card>
</section>
